import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  Truck, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Search, 
  Filter, 
  RotateCcw, 
  ChevronLeft, 
  ChevronRight,
  Calendar
} from "lucide-react";

const statusColors = {
  Delivered: "bg-emerald-100 text-emerald-800",
  Received: "bg-emerald-100 text-emerald-800",
  Shipped: "bg-blue-100 text-blue-800",
  "In Transit": "bg-blue-100 text-blue-800",
  Scheduled: "bg-slate-100 text-slate-800",
  Planned: "bg-slate-100 text-slate-800",
  Pending: "bg-amber-100 text-amber-800",
  Picked: "bg-amber-100 text-amber-800",
  Delayed: "bg-red-100 text-red-800",
};

const StatusBadge = (val) => <Badge className={`text-xs ${statusColors[val] || "bg-slate-100"}`}>{val}</Badge>;

const OTBadge = (val) => {
  if (val === true) return <span className="flex items-center justify-center gap-1 text-emerald-600"><CheckCircle className="w-3.5 h-3.5" /> Yes</span>;
  if (val === false) return <span className="flex items-center justify-center gap-1 text-red-500"><XCircle className="w-3.5 h-3.5" /> No</span>;
  return <span className="flex items-center justify-center gap-1 text-muted-foreground"><Clock className="w-3.5 h-3.5" /> Pending</span>;
};

const DeliveryTable = ({ title, data = [], columns }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const totalItems = data.length;
  const totalPages = Math.ceil(totalItems / pageSize) || 1;
  const safePage = Math.min(currentPage, totalPages);

  const paginatedData = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, safePage, pageSize]);

  return (
    <Card className="card-industrial">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Truck className="w-4 h-4 text-emerald-600" /> {title} ({totalItems})
        </CardTitle>
        {totalItems > 0 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="h-7 border rounded bg-background px-1 text-xs outline-none cursor-pointer"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        )}
      </CardHeader>
      <CardContent className="p-0">
        {totalItems === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No deliveries found matching your active filter criteria.
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    {columns.map((col) => (
                      <th key={col.key} className={`p-3 font-medium ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"}`}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row, idx) => (
                    <tr key={idx} className="border-b hover:bg-muted/30">
                      {columns.map((col) => (
                        <td key={col.key} className={`p-3 ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : ""}`}>
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between p-3 border-t bg-muted/20 text-xs text-muted-foreground">
                <div>
                  Showing {((safePage - 1) * pageSize) + 1} to {Math.min(safePage * pageSize, totalItems)} of {totalItems} entries
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={safePage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="w-3.5 h-3.5 mr-0.5" /> Previous
                  </Button>
                  <span className="px-2 font-medium">
                    Page {safePage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2"
                    disabled={safePage >= totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const DeliveryTracking = ({ deliveries, loading = false, onLoad }) => {
  const [viewType, setViewType] = useState("outbound");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [onTimeFilter, setOnTimeFilter] = useState("ALL");
  const [dateRange, setDateRange] = useState("");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const inboundRows = useMemo(() => deliveries?.inbound || [], [deliveries?.inbound]);
  const outboundRows = useMemo(() => deliveries?.outbound || [], [deliveries?.outbound]);

  const resetFilters = () => {
    setSearch("");
    setStatusFilter("ALL");
    setOnTimeFilter("ALL");
    setDateRange("");
    setCustomFrom("");
    setCustomTo("");
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const loadSelectedRange = () => {
    if (!dateRange || !onLoad) return;
    if (dateRange === "custom") {
      if (!customFrom || !customTo || customTo < customFrom) return;
      onLoad({ dateFrom: customFrom, dateTo: customTo });
      return;
    }
    const dateTo = new Date();
    const dateFrom = new Date(dateTo);
    dateFrom.setDate(dateFrom.getDate() - Number(dateRange) + 1);
    onLoad({ dateFrom: formatDate(dateFrom), dateTo: formatDate(dateTo) });
  };

  const filteredInbound = useMemo(() => {
    if (!inboundRows || inboundRows.length === 0) return [];
    const searchLower = search.trim().toLowerCase();
    const today = new Date();

    return inboundRows.filter((row) => {
      if (searchLower) {
        const delNum = String(row.delivery_number || "").toLowerCase();
        const vendor = String(row.vendor || "").toLowerCase();
        const customer = String(row.customer || "").toLowerCase();
        const customerName = String(row.customer_name || "").toLowerCase();
        const material = String(row.material || "").toLowerCase();
        const matchesSearch =
          delNum.includes(searchLower) ||
          vendor.includes(searchLower) ||
          customer.includes(searchLower) ||
          customerName.includes(searchLower) ||
          material.includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (statusFilter !== "ALL") {
        const rowStatus = String(row.status || "").toLowerCase();
        if (rowStatus !== statusFilter.toLowerCase()) return false;
      }

      if (onTimeFilter !== "ALL") {
        if (onTimeFilter === "yes" && row.on_time !== true) return false;
        if (onTimeFilter === "no" && row.on_time !== false) return false;
        if (onTimeFilter === "pending" && (row.on_time === true || row.on_time === false)) return false;
      }

      if (dateRange) {
        const rowDateStr = row.planned_date || row.actual_date;
        if (!rowDateStr) return false;

        const rowDate = new Date(rowDateStr);
        if (isNaN(rowDate.getTime())) return false;

        if (dateRange === "custom") {
          if (customFrom) {
            const fromDate = new Date(customFrom);
            if (rowDate < fromDate) return false;
          }
          if (customTo) {
            const toDate = new Date(customTo);
            toDate.setHours(23, 59, 59, 999);
            if (rowDate > toDate) return false;
          }
        } else {
          const daysLimit = Number(dateRange);
          if (!isNaN(daysLimit)) {
            const diffTime = today.getTime() - rowDate.getTime();
            const diffDays = diffTime / (1000 * 3600 * 24);
            if (diffDays < 0 || diffDays > daysLimit) return false;
          }
        }
      }

      return true;
    });
  }, [inboundRows, search, statusFilter, onTimeFilter, dateRange, customFrom, customTo]);

  const filteredOutbound = useMemo(() => {
    if (!outboundRows || outboundRows.length === 0) return [];
    const searchLower = search.trim().toLowerCase();
    const today = new Date();

    return outboundRows.filter((row) => {
      if (searchLower) {
        const delNum = String(row.delivery_number || "").toLowerCase();
        const vendor = String(row.vendor || "").toLowerCase();
        const customer = String(row.customer || "").toLowerCase();
        const customerName = String(row.customer_name || "").toLowerCase();
        const material = String(row.material || "").toLowerCase();
        const matchesSearch =
          delNum.includes(searchLower) ||
          vendor.includes(searchLower) ||
          customer.includes(searchLower) ||
          customerName.includes(searchLower) ||
          material.includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (statusFilter !== "ALL") {
        const rowStatus = String(row.status || "").toLowerCase();
        if (rowStatus !== statusFilter.toLowerCase()) return false;
      }

      if (onTimeFilter !== "ALL") {
        if (onTimeFilter === "yes" && row.on_time !== true) return false;
        if (onTimeFilter === "no" && row.on_time !== false) return false;
        if (onTimeFilter === "pending" && (row.on_time === true || row.on_time === false)) return false;
      }

      if (dateRange) {
        const rowDateStr = row.planned_date || row.actual_date;
        if (!rowDateStr) return false;

        const rowDate = new Date(rowDateStr);
        if (isNaN(rowDate.getTime())) return false;

        if (dateRange === "custom") {
          if (customFrom) {
            const fromDate = new Date(customFrom);
            if (rowDate < fromDate) return false;
          }
          if (customTo) {
            const toDate = new Date(customTo);
            toDate.setHours(23, 59, 59, 999);
            if (rowDate > toDate) return false;
          }
        } else {
          const daysLimit = Number(dateRange);
          if (!isNaN(daysLimit)) {
            const diffTime = today.getTime() - rowDate.getTime();
            const diffDays = diffTime / (1000 * 3600 * 24);
            if (diffDays < 0 || diffDays > daysLimit) return false;
          }
        }
      }

      return true;
    });
  }, [outboundRows, search, statusFilter, onTimeFilter, dateRange, customFrom, customTo]);

  const hasActiveFilters = Boolean(search || statusFilter !== "ALL" || onTimeFilter !== "ALL" || dateRange || customFrom || customTo);
  const canLoadRange = Boolean(dateRange && (dateRange !== "custom" || (customFrom && customTo && customTo >= customFrom)));

  const renderQty = (v, row) => {
    const deliv = v ?? 0;
    if (row.ordered_quantity && row.ordered_quantity > 0) {
      return `${deliv} / ${row.ordered_quantity}`;
    }
    return deliv;
  };

  const inboundCols = [
    { key: "delivery_number", label: "Delivery #" },
    { key: "vendor", label: "Vendor" },
    { key: "material", label: "Material" },
    { key: "quantity", label: "Qty", align: "right", render: renderQty },
    { key: "planned_date", label: "Planned", render: (v) => v || "-" },
    { key: "actual_date", label: "Actual", render: (v) => v || "-" },
    { key: "status", label: "Status", align: "center", render: StatusBadge },
    { key: "on_time", label: "On-Time", align: "center", render: OTBadge },
  ];

  const outboundCols = [
    { key: "delivery_number", label: "Delivery #" },
    { key: "customer", label: "Customer" },
    { key: "customer_name", label: "Customer Name", render: (v) => v || "-" },
    { key: "material", label: "Material" },
    { key: "quantity", label: "Qty", align: "right", render: renderQty },
    { key: "planned_date", label: "Planned", render: (v) => v || "-" },
    { key: "actual_date", label: "Actual", render: (v) => v || "-" },
    { key: "status", label: "Status", align: "center", render: StatusBadge },
    { key: "on_time", label: "On-Time", align: "center", render: OTBadge },
  ];

  return (
    <div className="space-y-4" data-testid="sc-delivery-tracking">
      {/* Sub-tab view mode toggles */}
      {deliveries && <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 bg-muted/60 p-1 rounded-lg border text-xs">
          <button
            type="button"
            onClick={() => setViewType("inbound")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-all ${
              viewType === "inbound"
                ? "bg-background text-foreground shadow-sm font-semibold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Truck className="w-3.5 h-3.5 text-emerald-600" />
            <span>Inbound Deliveries</span>
            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] bg-muted font-mono">
              {filteredInbound.length}
            </Badge>
          </button>
          <button
            type="button"
            onClick={() => setViewType("outbound")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-all ${
              viewType === "outbound"
                ? "bg-background text-foreground shadow-sm font-semibold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Truck className="w-3.5 h-3.5 text-blue-600" />
            <span>Outbound Deliveries</span>
            <Badge variant="secondary" className="ml-1 px-1.5 py-0 text-[10px] bg-muted font-mono">
              {filteredOutbound.length}
            </Badge>
          </button>
          <button
            type="button"
            onClick={() => setViewType("all")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md font-medium transition-all ${
              viewType === "all"
                ? "bg-background text-foreground shadow-sm font-semibold"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <span>All Deliveries</span>
          </button>
        </div>
      </div>}

      {/* Deliveries Filter Control Bar */}
      <Card className="p-3.5 bg-card shadow-sm border">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-semibold text-sm">
              <Filter className="w-4 h-4 text-emerald-600" />
              <span>Deliveries Filter Controls</span>
            </div>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                className="h-7 text-xs text-muted-foreground hover:text-foreground"
              >
                <RotateCcw className="w-3 h-3 mr-1" /> Reset Filters
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2.5 text-xs">
            {/* Status Filter */}
            <div className="flex items-center gap-1.5 border rounded-md px-2.5 h-8 bg-background w-full sm:w-44">
              <span className="text-muted-foreground whitespace-nowrap">Status:</span>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-transparent font-medium text-foreground outline-none w-full cursor-pointer text-xs"
              >
                <option value="ALL">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="In Transit">In Transit</option>
                <option value="Received">Received</option>
                <option value="Delivered">Delivered</option>
                <option value="Shipped">Shipped</option>
                <option value="Planned">Planned</option>
              </select>
            </div>

            {/* On-Time Filter */}
            <div className="flex items-center gap-1.5 border rounded-md px-2.5 h-8 bg-background w-full sm:w-48">
              <span className="text-muted-foreground whitespace-nowrap">On-Time:</span>
              <select
                value={onTimeFilter}
                onChange={(e) => setOnTimeFilter(e.target.value)}
                className="bg-transparent font-medium text-foreground outline-none w-full cursor-pointer text-xs"
              >
                <option value="ALL">All Statuses</option>
                <option value="yes">Yes (On-Time)</option>
                <option value="no">No (Delayed)</option>
                <option value="pending">Pending</option>
              </select>
            </div>

            {/* Date Range Preset */}
            <div className="flex items-center gap-1.5 border rounded-md px-2.5 h-8 bg-background w-full sm:w-48">
              <Calendar className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="bg-transparent font-medium text-foreground outline-none w-full cursor-pointer text-xs"
              >
                <option value="">Select a date range</option>
                <option value="30">Last 30 Days</option>
                <option value="60">Last 60 Days</option>
                <option value="90">Last 90 Days</option>
                <option value="custom">Custom Date Range</option>
              </select>
            </div>

            {/* Load Deliveries Button aligned on the same line */}
            <div className="w-full sm:w-auto sm:ml-auto">
              <Button
                size="sm"
                className="h-8 px-4 text-xs whitespace-nowrap w-full sm:w-auto"
                onClick={loadSelectedRange}
                disabled={!canLoadRange || loading}
              >
                {loading ? "Loading..." : "Load Deliveries"}
              </Button>
            </div>
          </div>

          {/* Custom Date Inputs */}
          {dateRange === "custom" && (
            <div className="flex items-center gap-2 pt-1.5 text-xs border-t border-dashed">
              <span className="text-muted-foreground font-medium">From:</span>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="h-7 w-36 text-xs"
              />
              <span className="text-muted-foreground font-medium">To:</span>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="h-7 w-36 text-xs"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Search Input - Positioned between Filter Container and Data Container */}
      <div className="flex items-center justify-between gap-3" data-testid="deliveries-search-container">
        <div className="relative w-full max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search Delivery #, Customer, Vendor, Material..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-8 h-9 text-xs bg-card shadow-xs rounded-lg border focus-visible:ring-1"
            data-testid="deliveries-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2.5 top-2 text-sm text-muted-foreground hover:text-foreground font-bold"
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex h-64 items-center justify-center rounded-lg border bg-card text-sm text-muted-foreground">
          <Clock className="mr-2 h-5 w-5 animate-spin" /> Loading delivery details from SAP...
        </div>
      )}
      {!loading && !deliveries && (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Select a date range and click <span className="font-medium text-foreground">Load Deliveries</span> to retrieve SAP delivery data.
        </Card>
      )}
      {!loading && deliveries && (viewType === "inbound" || viewType === "all") && (
        <DeliveryTable title="Inbound Deliveries" data={filteredInbound} columns={inboundCols} />
      )}
      {!loading && deliveries && (viewType === "outbound" || viewType === "all") && (
        <DeliveryTable title="Outbound Deliveries" data={filteredOutbound} columns={outboundCols} />
      )}
    </div>
  );
};

export default DeliveryTracking;
